import React, { useMemo, useRef, useState } from "react";
import subjectsData from '../../data/subjects.json';
import { Field } from "../components/FormControls";
import { contactUsFieldErrors, fieldError, isOrderNumberRequired } from "../../utils/validationUtils";
import "./ContactUsForm.css";
import SuccessPopup from "../components/SuccessPopup";
import { contactUsJson } from "../../api/api";
import arrowLeft from '../../assets/arrow-left.png';
import superpharmLogo from '../../assets/superpharm-logo.svg';

const ONLINE_SUBJECT_ID = 'online';

const initial = {
    subject: '',
    subSubject: '',
    orderNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
};

const SUBJECT_OPTIONS = [
    ...subjectsData.subjects.map((s: any) => ({
        value: s.id,
        label: s.label,
    })),
];

const SUB_SUBJECT_OPTIONS: Record<string, { value: string; label: string; requiresOrderNumber?: boolean }[]> = {};
subjectsData.subjects.forEach((s: any) => {
    SUB_SUBJECT_OPTIONS[s.id] = s.subSubjects.map((sub: any) => ({
        value: sub.id,
        label: sub.label,
        requiresOrderNumber: sub.requiresOrderNumber,
    }));
});

const ONLINE_SUB_SUBJECTS = SUB_SUBJECT_OPTIONS[ONLINE_SUBJECT_ID] ?? [];

export default function ContactUsForm() {
    const [form, setForm] = useState(initial);
    const [errors, setErrors] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    /** Server message when success === false (duplicate); no regular success UI in that case */
    const [infoMessage, setInfoMessage] = useState<string | undefined>(undefined);
    const [subjectOpen, setSubjectOpen] = useState(false);
    const subjectButtonRef = useRef<HTMLButtonElement | null>(null);

    const isOnlineSubject = form.subject === ONLINE_SUBJECT_ID;
    // Hide order number only for "סיוע בביצוע הזמנה"; keep it visible until that sub-subject is chosen
    const showOrderNumber = isOnlineSubject && (
        !form.subSubject ||
        isOrderNumberRequired(form.subject, form.subSubject, ONLINE_SUB_SUBJECTS)
    );

    const selectedSubjectOption = useMemo(() => {
        return SUBJECT_OPTIONS.find(opt => opt.value === form.subject);
    }, [form.subject]);

    const setSubjectValue = (nextValue: string) => {
        setForm(f => ({
            ...f,
            subject: nextValue,
            subSubject: '',
            orderNumber: '',
        }));
        setErrors((er: any) => ({
            ...er,
            subject: fieldError('subject', nextValue),
            subSubject: undefined,
            orderNumber: undefined,
        }));
    };

    const toggleSubject = () => {
        setSubjectOpen(v => !v);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        setForm(f => {
            if (name === 'subSubject') {
                const orderRequired = isOrderNumberRequired(f.subject, value, ONLINE_SUB_SUBJECTS);
                return {
                    ...f,
                    subSubject: value,
                    // Clear order number when it is not needed for the selected sub-subject
                    orderNumber: orderRequired ? f.orderNumber : '',
                };
            }
            return { ...f, [name]: value };
        });

        setErrors((er: any) => {
            const updated = { ...er, [name]: fieldError(name, value) };

            if (name === 'subSubject') {
                updated.orderNumber = undefined;
            }

            if (name === 'orderNumber' && !isOrderNumberRequired(form.subject, form.subSubject, ONLINE_SUB_SUBJECTS)) {
                updated.orderNumber = undefined;
            }

            return updated;
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        const focusErrors = contactUsFieldErrors(form, ONLINE_SUB_SUBJECTS);
        setErrors(focusErrors);
        if (Object.values(focusErrors).some(Boolean)) {
            setLoading(false);
            return;
        }

        const subjectLabel = SUBJECT_OPTIONS.find(opt => opt.value === form.subject)?.label || form.subject;
        const subSubjectLabel = SUB_SUBJECT_OPTIONS[form.subject]?.find(opt => opt.value === form.subSubject)?.label || form.subSubject;

        const payload = {
            ...form,
            subjectId: form.subject,
            subject: subjectLabel,
            subSubjectId: form.subSubject,
            subSubject: subSubjectLabel,
            orderNumber: form.orderNumber.trim() || undefined,
        };

        try {
            const data = await contactUsJson(payload) as { success: boolean; message?: string };
            if (data.success === false && data.message) {
                setInfoMessage(data.message);
                setShowSuccess(true);
                return;
            }
            setInfoMessage(undefined);
            setShowSuccess(true);
            setForm({ ...initial });
            setErrors({});
        } catch (error) {
            console.error(`Failed to submit contact us form`, error);
            alert("שגיאה בשליחת הטופס. אנא נסה שוב מאוחר יותר.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <main dir="rtl" className="page">
            <SuccessPopup
                open={showSuccess}
                infoMessage={infoMessage}
                onClose={() => {
                    setShowSuccess(false);
                    setInfoMessage(undefined);
                }}
            />
            <div className="container">
                <div className="contact-card">
                    <div className="contact-title-wrap">
                        <div className="contact-header-row">
                            <button
                                type="button"
                                className="contact-back-btn"
                                aria-label="חזרה"
                                onClick={() => {
                                    window.location.href = process.env.REACT_APP_LOBBY_LINK || 'https://glassix.consist.co.il/Superpharm-iframes/lobby';
                                }}
                            >
                                <img src={arrowLeft} alt="" className="contact-back-icon" />
                            </button>
                            <img src={superpharmLogo} alt="Super-Pharm" className="contact-logo" />
                        </div>
                        <div className="title-wrap">
                            <button
                                type="button"
                                className="contact-back-btn"
                                aria-label="חזרה"
                                onClick={() => {
                                    window.location.href = process.env.REACT_APP_LOBBY_LINK || 'https://glassix.consist.co.il/Superpharm-iframes/lobby';
                                }}
                            >
                                <img src={arrowLeft} alt="" className="contact-arrow" style={{ width: 24, height: 24, transform: 'rotate(0deg)', opacity: 1, marginTop: '4px' }} />
                            </button>
                            <h1 className="contact-title">טופס יצירת קשר</h1>
                        </div>
                        <div className="contact-subtitle-wrap">
                            <div className="contact-subtitle">
                                תודה שפנית אלינו.<br />
                                על מנת שנוכל לטפל בפנייתך, אנא מלא/י את הפרטים מטה.<br />
                                המענה ישלח למייל.
                            </div>
                        </div>
                    </div>
                    <form className="contact-form" onSubmit={handleSubmit} onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target instanceof HTMLElement && e.target.tagName !== 'BUTTON' && e.target.tagName !== 'TEXTAREA') {
                            e.preventDefault();
                        }
                    }}>
                        <div className={[
                            'contact-top-fields',
                            isOnlineSubject ? 'contact-top-fields-online' : '',
                            isOnlineSubject && !showOrderNumber ? 'contact-top-fields-online-no-order' : '',
                        ].filter(Boolean).join(' ')}>
                            <div className="contact-field contact-field-subject">
                                <div className="input-float-wrap select-float-wrap">
                                    <label className="input-float-label-contactus" htmlFor="subject">
                                        נושא הפנייה<span className="req">*</span>
                                    </label>
                                    <button
                                        ref={subjectButtonRef}
                                        id="subject"
                                        type="button"
                                        className={`contact-select input-float ${errors.subject ? "input-error" : ""}`}
                                        aria-haspopup="listbox"
                                        aria-expanded={subjectOpen}
                                        aria-controls="subject-listbox"
                                        onClick={toggleSubject}
                                    >
                                        <span className={`contact-select-value ${!form.subject ? "is-placeholder" : ""}`}>
                                            {selectedSubjectOption?.label || "בחרו נושא"}
                                        </span>
                                    </button>
                                    {subjectOpen && (
                                        <ul id="subject-listbox" role="listbox" className="contact-select-menu">
                                            {SUBJECT_OPTIONS.map((opt) => (
                                                <li
                                                    key={opt.value}
                                                    role="option"
                                                    aria-selected={opt.value === form.subject}
                                                    className={[
                                                        "contact-select-option",
                                                        opt.value === form.subject ? "is-selected" : "",
                                                    ].filter(Boolean).join(" ")}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        setSubjectValue(opt.value);
                                                        setSubjectOpen(false);
                                                        subjectButtonRef.current?.focus();
                                                    }}
                                                >
                                                    {opt.label}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {errors.subject && <div className="error-text">{errors.subject}</div>}
                                </div>
                            </div>
                            {isOnlineSubject && (
                                <div className="contact-field contact-field-subSubject">
                                    <div className="input-float-wrap select-float-wrap">
                                        <label className="input-float-label-contactus" htmlFor="subSubject">
                                            נושא משני<span className="req">*</span>
                                        </label>
                                        <select
                                            id="subSubject"
                                            name="subSubject"
                                            value={form.subSubject}
                                            onChange={handleChange}
                                            className={`input input-float ${errors.subSubject ? "input-error" : ""}`}
                                        >
                                            <option value="">בחרו נושא משני</option>
                                            {SUB_SUBJECT_OPTIONS[form.subject]?.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        {errors.subSubject && <div className="error-text">{errors.subSubject}</div>}
                                    </div>
                                </div>
                            )}
                            {showOrderNumber && (
                                <div className="contact-field contact-field-orderNumber">
                                    <Field
                                        id="orderNumber"
                                        name="orderNumber"
                                        label="מספר הזמנה"
                                        inputMode="numeric"
                                        value={form.orderNumber}
                                        onChange={handleChange}
                                        error={errors.orderNumber}
                                        labelClassName="input-float-label-contactus"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="contact-form-grid">
                            <div className="contact-field contact-field-firstName">
                                <Field id="firstName" name="firstName" label="שם פרטי" value={form.firstName} onChange={handleChange} error={errors.firstName} labelClassName="input-float-label-contactus" />
                            </div>
                            <div className="contact-field contact-field-lastName">
                                <Field id="lastName" name="lastName" label="שם משפחה" value={form.lastName} onChange={handleChange} error={errors.lastName} labelClassName="input-float-label-contactus" />
                            </div>
                            <div className="contact-field contact-field-email">
                                <Field id="email" name="email" label="אימייל" value={form.email} onChange={handleChange} error={errors.email} labelClassName="input-float-label-contactus" />
                            </div>
                            <div className="contact-field contact-field-phone">
                                <Field id="phone" name="phone" label="טלפון" value={form.phone} onChange={handleChange} error={errors.phone} labelClassName="input-float-label-contactus" />
                            </div>
                            <div className="contact-field contact-field-message">
                                <Field id="message" name="message" label="איך אפשר לעזור?" value={form.message} onChange={handleChange} error={errors.message} labelClassName="input-float-label-contactus" />
                            </div>
                        </div>
                        <div className="contact-submit">
                            <button type="submit" className="contact-btn" disabled={loading}>
                                {loading ? "שולח..." : "שליחה"}
                                <img src={arrowLeft} alt="" className="send-arrow" style={{ width: 24, height: 24, transform: 'rotate(180deg)', opacity: 1 }} />
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </main>
    );
}
